import type { MetaNodeDef } from '../../game/meta';

/* ═══════════════════════════════════════════════════════════════════════════
   L'arbre de méta-progression — 40 nœuds.

   Deux natures, deux monnaies, et surtout deux RÔLES qu'il ne faut jamais
   confondre :

     · 8 nœuds STAT (points de compétence) — de la PUISSANCE, plafonnée.
       Ils comptent dans la jauge « PUISSANCE PERMANENTE x % / 35 % » affichée
       en permanence. Rendables gratuitement et à volonté.

     · 32 nœuds OPTION (crédits) — du CONTENU : vaisseaux, armes, déblocages.
       Ils n'ajoutent aucun chiffre au plafond. C'est ce qui permet à l'arbre de
       continuer à récompenser le volume joué sans que la difficulté du jeu
       s'écroule : on gagne des CHOIX, pas des pourcentages.

   ── POURQUOI L'ARBRE GROSSIT UNIQUEMENT PAR SA MOITIÉ OPTION ──

   ⚠️ La question n'est pas « où mettre seize nœuds de plus », c'est « comment
   ajouter seize nœuds sans toucher à la jauge ». Le plafond composite de +35 %
   est atteint EXACTEMENT par les huit nœuds STAT (voir le calibrage ci-dessous)
   et il n'y a pas un dixième de point à distribuer : tout nœud supplémentaire
   qui verserait un pourcentage exigerait d'en retirer autant ailleurs, donc de
   diluer une branche déjà calibrée pour se sentir à chaque palier.

   La sortie n'est pas une redistribution, c'est un changement de nature. Les
   nœuds ajoutés ne donnent presque AUCUN chiffre : ils donnent des armes
   secondaires (une seule s'équipe à la fois), des bombes (une seule par run),
   un vaisseau (un seul se pilote), des contraintes volontaires (qui RETIRENT
   de la puissance contre des crédits) et des outils d'entraînement (qui ne
   rapportent rien).

   Les deux SEULS à porter un chiffre — les améliorations de secondaire — le
   font HORS jauge, et l'exception est raisonnée : l'axe des secondaires est
   tenu sous la primaire par `validateSecondaries`, donc l'affûter n'achète pas
   la victoire, pas plus que débloquer une arme secondaire ne la vend. C'est du
   confort tactique, borné par `computeStats` (plafond des dégâts, plancher de
   recharge), et `compositePower` l'ignore par construction. La jauge reste donc
   à 35 % avec l'arbre entier acheté, ce que `verifyTree` vérifie au chargement
   et en CI.

   C'est la lecture littérale de §6.9 : la méta est une couche de rétention et
   de VARIÉTÉ, jamais une condition de victoire — un joueur excellent doit
   franchir le jeu à méta zéro. Seize nœuds de pourcentages auraient contredit
   cette phrase quel que soit le soin apporté à leur équilibrage.

   ⚠️ Écart assumé avec la lettre du plan : §6.9 annonce « 24 nœuds — 8 STAT +
   16 OPTION ». On en compte 40, parce que le jalon demande dix-huit nœuds
   OPTION EN PLUS des six existants, auxquels s'ajoutent la voie bouclier (deux
   nœuds) et QUATRE armes primaires à type de balle distinct (le Traqueur guidé,
   puis le Perforant, le Ricochet et l'Éventail). L'écart est de comptage, jamais
   de nature : la moitié STAT reste à huit nœuds et à +35 %, qui est la seule
   promesse que le plan fait au joueur.

   ── CALIBRAGE DES BRANCHES STAT ──

   Les trois branches somment EXACTEMENT aux plafonds du plan (§6.9) :
     dégâts   0,05 + 0,05 + 0,05 = 0,15
     PV       0,07 + 0,06 + 0,07 = 0,20
     vitesse  0,04 + 0,04        = 0,08
   Composite pondéré à saturation : 0,15 + 0,6 × 0,20 + 0,08 = 0,35 — la jauge
   se remplit pile à la complétion. `verifyTree` le vérifie en CI ; toucher un
   chiffre ici sans toucher le plafond fait échouer le build, ce qui est le but.

   Coût total : 17 points de compétence pour 22 distribués par les jalons.

   ⚠️ La branche PV paie D'UN SEUL COUP à son dernier palier — une vie ne se
   coupe pas en morceaux (voir `metaLivesBonus`). Les textes des paliers I et II
   annoncent donc une PROGRESSION, jamais un gain immédiat : un joueur qui
   achète « +7 % de PV » et ne voit rien changer conclut, à juste titre, que le
   jeu lui a menti.

   ── CALIBRAGE DES NŒUDS OPTION ──

   28 550 crédits au total, répartis en neuf familles :

     armes secondaires (formes)     5 nœuds   2 600 cr
     armes secondaires (conduites)  4 nœuds   3 150 cr
     améliorations de secondaire    2 nœuds     600 cr
     bouclier permanent             2 nœuds     700 cr
     bombes                         2 nœuds   1 200 cr
     vaisseaux                      3 nœuds   6 400 cr
     armes primaires                6 nœuds   9 000 cr
     contraintes volontaires        4 nœuds   2 050 cr
     confort et entraînement        4 nœuds   2 850 cr

   Une run correcte rapporte 600 à 900 crédits, un parcours complet propre
   1 200 à 1 500 (voir `creditsForRun`) : l'arbre entier représente une
   vingtaine de runs, la cible du plan. Le premier nœud coûte 250 — il tombe
   AVANT la fin du premier run raté, ce qui est la seule fenêtre où l'on peut
   encore convaincre un joueur que la progression existe.

   ⚠️ Les prérequis sont TOUS des jalons, jamais des achats. Ouvrir une arme en
   échange d'un nœud de statistique acheté est l'erreur documentée de Sky Force
   Reloaded : le joueur paie pour avoir le droit de payer. Ici on ouvre par
   objectif accompli — le champ `after` ne sert qu'à ordonner les paliers d'une
   même branche, et `verifyTree` refuse tout autre usage.

   ⚠️ `grants` est un identifiant de CONTENU, pas un identifiant de nœud, et il
   doit rester unique dans tout l'arbre : deux nœuds qui ouvrent la même chose
   font payer deux fois pour un seul gain. Convention par famille :
     · arme secondaire  → l'identifiant EXACT du `SecondaryDef`, que
                          `secondaryByI` résout sans table de correspondance ;
     · arme primaire, vaisseau → une clé courte (`plasma`, `aegis`) ;
     · le reste         → un préfixe de famille (`bombe-`, `contrainte-`).
   ═══════════════════════════════════════════════════════════════════════════ */

const tree: MetaNodeDef[] = [
  /* ── Branche DÉGÂTS : 0,15 au total ──────────────────────────────────────
     Volontairement la moins chère à démarrer. C'est la statistique dont l'effet
     se SENT immédiatement — les popcorns tombent en un tir de moins — et le
     premier achat d'un arbre doit se sentir, sinon le joueur ne fait pas le
     deuxième. */
  {
    kind: 'stat',
    currency: 'skill',
    id: 'canon-1',
    name: 'Surtension I',
    text: '+5 % de dégâts.',
    branch: 'damage',
    bonus: 0.05,
    cost: 1,
    after: null,
    requires: [],
  },
  {
    kind: 'stat',
    currency: 'skill',
    id: 'canon-2',
    name: 'Surtension II',
    text: '+5 % de dégâts (10 % au total).',
    branch: 'damage',
    bonus: 0.05,
    cost: 2,
    after: 'canon-1',
    requires: ['monde-1-franchi'],
  },
  {
    kind: 'stat',
    currency: 'skill',
    id: 'canon-3',
    name: 'Surtension III',
    text: '+5 % de dégâts (15 % au total — plafond de la branche).',
    branch: 'damage',
    bonus: 0.05,
    cost: 3,
    after: 'canon-2',
    requires: ['monde-2-franchi'],
  },

  /* ── Branche PV : 0,20 au total, versée en une vie au dernier palier ──── */
  {
    kind: 'stat',
    currency: 'skill',
    id: 'coque-1',
    name: 'Blindage I',
    text: 'Renfort de coque : 7 % des 20 % nécessaires à une vie supplémentaire.',
    branch: 'maxHealth',
    bonus: 0.07,
    cost: 1,
    after: null,
    requires: [],
  },
  {
    kind: 'stat',
    currency: 'skill',
    id: 'coque-2',
    name: 'Blindage II',
    text: 'Renfort de coque : 13 % des 20 % nécessaires à une vie supplémentaire.',
    branch: 'maxHealth',
    bonus: 0.06,
    cost: 2,
    after: 'coque-1',
    requires: ['monde-1-franchi'],
  },
  {
    kind: 'stat',
    currency: 'skill',
    id: 'coque-3',
    name: 'Blindage III',
    text: 'Coque complète : +1 vie au départ de chaque run.',
    branch: 'maxHealth',
    bonus: 0.07,
    cost: 3,
    after: 'coque-2',
    requires: ['run-sans-mort'],
    // Gaté par « finir sans mourir » et pas par un monde : la vie
    // supplémentaire est la récompense la plus proche d'un filet de sécurité,
    // c'est donc celle qui doit le moins ressembler à un achat de survie. On la
    // donne à qui a déjà prouvé qu'il pouvait s'en passer.
  },

  /* ── Branche VITESSE : 0,08 au total ─────────────────────────────────────
     La plus courte et la plus chère au palier. La vitesse de déplacement est la
     statistique d'esquive : c'est celle qui change le plus le ressenti de
     difficulté à chiffre égal, donc celle qu'on ouvre le plus tard. */
  {
    kind: 'stat',
    currency: 'skill',
    id: 'propulsion-1',
    name: 'Propulsion I',
    text: '+4 % de vitesse de déplacement.',
    branch: 'moveSpeed',
    bonus: 0.04,
    cost: 2,
    after: null,
    requires: ['monde-1-franchi'],
  },
  {
    kind: 'stat',
    currency: 'skill',
    id: 'propulsion-2',
    name: 'Propulsion II',
    text: '+4 % de vitesse de déplacement (8 % au total — plafond de la branche).',
    branch: 'moveSpeed',
    bonus: 0.04,
    cost: 3,
    after: 'propulsion-1',
    requires: ['frolement-200'],
  },

  /* ── Nœuds OPTION : le contenu, payé en crédits ─────────────────────────── */
  /* ⚠️ « Boutique de bord » (grants: 'shop') a été RETIRÉE de l'arbre.

     Elle y figurait à 250 crédits, s'affichait « ACQUIS » à l'achat, et
     n'ouvrait rien : la boutique en cours de partie n'existe pas. Elle faisait
     partie des quinze nœuds sur vingt-deux dont le `grants` désignait du
     contenu inexistant — un joueur pouvait y engloutir plusieurs milliers de
     crédits sans qu'aucun écran ne change.

     Le choix ici est de la retirer plutôt que de l'improviser. Une boutique
     touche à l'économie des cellules, au rythme entre deux mondes et au
     `cellsSpent` du bilan de partie ; bâclée, elle coûterait plus cher à
     défaire qu'à écrire. Un arbre plus court dont chaque nœud tient sa
     promesse vaut mieux qu'un arbre complet dont deux tiers mentent.

     `RunRecord.cellsSpent` reste en place et vaut zéro : c'est le point
     d'accroche du jour où la boutique existera. */
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-lance-plasma',
    name: 'Lance-plasma',
    text: 'Arme de départ alternative : moins de cadence, dégâts par salve élevés.',
    option: 'weapon',
    grants: 'plasma',
    cost: 900,
    after: null,
    requires: ['monde-1-franchi'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'relance-supplementaire',
    name: 'Protocole de relance',
    text: 'Une relance de plus à chaque écran de cartes.',
    option: 'unlock',
    grants: 'reroll+1',
    cost: 1000,
    after: null,
    requires: ['chasseur-400'],
    // Un déblocage qui augmente la QUALITÉ des choix et non la puissance : il
    // ne compte pas dans le plafond parce qu'il ne donne aucun chiffre, il rend
    // seulement le build voulu plus atteignable.
  },
  /* ── Le second arsenal ────────────────────────────────────────────────────
     Quatre armes secondaires dont le COMPORTEMENT diffère, là où les six
     premières ne variaient que par la géométrie de la volée. Elles sont vendues
     plus cher qu'elles ne valent en dégâts, et c'est voulu : ce qu'on achète
     ici n'est pas de la puissance mais une RÉPONSE à une situation — une cible
     qui fuit, un rideau qu'on traverse, un groupe aligné.

     ⚠️ Elles arrivent après « monde 1 franchi ». Un joueur qui découvre encore
     le clic droit n'a aucun moyen de choisir entre un missile guidé et un rayon
     continu : lui ouvrir les quatre d'emblée ne lui offrirait pas un choix,
     seulement une liste. */
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-missiles-traqueurs',
    name: 'Missiles traqueurs',
    text: 'Arme secondaire : une salve qui infléchit sa course vers l’ennemi le plus proche. 70 dégâts par impact, recharge 5,5 s.',
    option: 'weapon',
    grants: 'missiles-traqueurs',
    cost: 700,
    after: null,
    requires: ['monde-1-franchi'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-rayon-continu',
    name: 'Rayon continu',
    text: 'Arme secondaire : un faisceau qui persiste et balaie, au lieu d’une salve. 9 dégâts par impulsion, recharge 8 s.',
    option: 'weapon',
    grants: 'rayon-continu',
    cost: 800,
    after: null,
    requires: ['monde-1-franchi'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-scies-orbitales',
    name: 'Scies orbitales',
    text: 'Arme secondaire : des disques qui traversent plusieurs ennemis avant de s’éteindre. 45 dégâts par cible, recharge 6 s.',
    option: 'weapon',
    grants: 'scies-orbitales',
    cost: 750,
    after: null,
    requires: ['monde-1-franchi'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-orage-electrique',
    name: 'Orage électrique',
    text: 'Arme secondaire : un arc qui saute d’un ennemi au suivant. 55 dégâts par saut, recharge 4,5 s.',
    option: 'weapon',
    grants: 'orage-electrique',
    cost: 900,
    after: null,
    requires: ['monde-2-franchi'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'vaisseau-lance',
    name: 'Vaisseau LANCE',
    // ⚠️ « hitbox réduite » a été RETIRÉ de cette promesse. Le rayon de la
    // hitbox est une constante de `FEEL` et non une statistique, et le point
    // blanc de neuf unités toujours affiché est le contrat de lisibilité que le
    // jeu ne négocie jamais. Le rendre variable exigerait de le faire varier
    // aussi à l'écran, sous peine de montrer un point et d'en toucher un autre.
    // Corriger le texte coûtait moins cher que d'entailler la règle.
    text: 'Coque fragile : une vie de moins, un quart de cadence en plus.',
    option: 'ship',
    grants: 'lance',
    cost: 1400,
    after: null,
    requires: ['monde-2-franchi'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-faisceau',
    name: 'Faisceau continu',
    text: 'Arme de départ alternative : flux permanent, perforant, portée courte.',
    option: 'weapon',
    grants: 'faisceau',
    cost: 2200,
    after: null,
    requires: ['boss-sans-bombe'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-traqueur',
    name: 'Traqueur',
    text: 'Arme de départ alternative : balles GUIDÉES qui suivent les cibles mobiles.',
    option: 'weapon',
    grants: 'traqueur',
    cost: 1800,
    after: null,
    // « 400 ennemis dans une run » : le joueur qui engage beaucoup, à qui la visée
    // assistée sur cibles mobiles rapporte le plus.
    requires: ['chasseur-400'],
  },
  /* ── Les trois armes primaires à TYPE DE BALLE distinct ────────────────────
     Elles étendent l'axe ouvert par le Traqueur : une arme se distingue par la
     NATURE de son projectile, jamais par sa puissance nominale (toutes à 200 DPS,
     vérifié par `validateWeapons`). Deux d'entre elles — Perforant et Ricochet —
     touchent plusieurs cibles ; leur DPS multi-cible est BORNÉ par la décroissance
     de dégâts du tir primaire (voir collisionSystem) et vérifié par
     `tests/balance/multicible.test.ts`. Gatées par des jalons qui désignent le
     joueur à qui chacune parle : celui qui engage (Perforant), celui qui esquive
     proprement (Ricochet), celui qui frôle (Éventail). */
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-perforant',
    name: 'Perforant',
    text: 'Arme de départ alternative : rounds qui TRAVERSENT une file d’ennemis alignés.',
    option: 'weapon',
    grants: 'perforant',
    cost: 1500,
    after: null,
    // « 400 ennemis dans une run » : le joueur qui affronte des files serrées, la
    // situation exacte que la perforation récompense.
    requires: ['chasseur-400'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-ricochet',
    name: 'Ricochet',
    text: 'Arme de départ alternative : rounds qui SAUTENT d’un ennemi groupé au suivant.',
    option: 'weapon',
    grants: 'ricochet',
    cost: 1500,
    after: null,
    // « boss vaincu sans être touché » : l'arc exige d'entrer dans les groupes,
    // donc de savoir esquiver de près. On l'ouvre à qui l'a déjà prouvé.
    requires: ['boss-sans-degat'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'arme-eventail',
    name: 'Éventail',
    text: 'Arme de départ alternative : tir en ÉVENTAIL large, redoutable sur les groupes dispersés.',
    option: 'weapon',
    grants: 'eventail',
    cost: 1100,
    after: null,
    // « 200 frôlements » : le joueur qui joue au contact des nuées, à qui une
    // large couverture rapporte plus qu'une colonne mono-cible.
    requires: ['frolement-200'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'vaisseau-aegis',
    name: 'Vaisseau AEGIS',
    text: 'Lent et lourdement blindé, bombe rechargeable au frôlement.',
    option: 'ship',
    grants: 'aegis',
    cost: 3200,
    after: null,
    requires: ['monde-3-franchi'],
    // Le nœud terminal de l'arbre crédits : il demande à la fois d'avoir tenu
    // jusqu'au monde 3 et d'avoir accumulé le plus gros montant. C'est un
    // objectif de complétion, jamais un passage obligé — AEGIS n'est pas plus
    // fort que le VULCAN de départ, il se joue autrement.
  },

  /* ── Armes SECONDAIRES ────────────────────────────────────────────────────
     Cinq nœuds pour cinq armes, et zéro pourcentage.

     Le catalogue en compte six ; `DEFAULT_SECONDARY_ID` en équipe une d'office
     (la Lance perforante) et c'est la plus forte du lot en DPS théorique — 120
     contre 80 à 116 pour les cinq autres. Débloquer une secondaire n'est donc
     jamais une montée en puissance, c'est un ÉLARGISSEMENT : chacune répond à
     une situation que la Lance ne traite pas, aucune ne traite mieux ce que la
     Lance traite déjà.

     Ce sont les nœuds les moins chers de l'arbre, et volontairement : une arme
     qu'on n'a pas essayée ne vaut rien, et le prix d'entrée d'un essai doit
     rester sous le revenu d'une seule run. Le cher, dans une secondaire, ce
     n'est pas le crédit — c'est la cinquantaine de runs qu'il faut pour savoir
     quand la déclencher. ── */
  {
    kind: 'option',
    currency: 'credits',
    id: 'sec-flanqueurs',
    name: 'Drones flanqueurs',
    text: 'Arme secondaire : 2 × 3 × 55 dégâts sur les flancs, recharge 4 s.',
    option: 'weapon',
    grants: 'drones-flanqueurs',
    cost: 300,
    after: null,
    requires: [],
    // Sans prérequis, comme la boutique : c'est la première arme que le joueur
    // peut s'offrir, et elle répond au seul angle mort qu'il aura déjà subi
    // sans comprendre pourquoi — le Rôdeur qui passe à côté et qu'on ne peut
    // pas toucher. Une deuxième porte d'entrée dans l'arbre, à côté de la
    // boutique : on ne fait jamais confiance à un système de progression qui
    // n'offre qu'un seul premier achat.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'sec-mines',
    name: 'Mines ascendantes',
    text: 'Arme secondaire : 4 mines × 160 dégâts en montée lente, recharge 6 s.',
    option: 'weapon',
    grants: 'mines-ascendantes',
    cost: 400,
    after: null,
    requires: ['monde-1-franchi'],
    // Gaté par le monde 1 pour une raison de fond : les mines se posent en
    // prévision d'une vague qu'on connaît. Les ouvrir à un joueur qui n'a
    // jamais fini un monde, c'est lui vendre une arme qui ne peut rien faire
    // pour lui — et un achat qui ne fait rien est un achat qui apprend au
    // joueur à ne plus acheter.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'sec-riposte',
    name: 'Riposte rapprochée',
    text: 'Arme secondaire : 2 × 5 × 28 dégâts en couronne autour du vaisseau, recharge 3,5 s.',
    option: 'weapon',
    grants: 'riposte-rapprochee',
    cost: 500,
    after: null,
    requires: ['chasseur-400'],
    // « Détruire 400 ennemis dans une run » désigne exactement le joueur qui en
    // a besoin : celui qui tient la pression assez longtemps pour se faire
    // dépasser par le bas.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'sec-trainee',
    name: 'Traînée de salves',
    text: 'Arme secondaire : 12 salves de 2 × 25 dégâts sur 2,2 s le long du trajet, recharge 6 s.',
    option: 'weapon',
    grants: 'trainee-de-salves',
    cost: 600,
    after: null,
    requires: ['monde-2-franchi'],
    // Le monde 2 est celui du Tisseur et de ses murs — la situation que cette
    // arme traite. Le prérequis n'est pas une barrière de temps, c'est la
    // garantie que le joueur a déjà rencontré le problème avant qu'on lui en
    // vende la solution.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'sec-faisceau-bref',
    name: 'Faisceau bref',
    text: 'Arme secondaire : 18 × 45 dégâts en 0,5 s, faisceau continu, recharge 7 s.',
    option: 'weapon',
    grants: 'faisceau-bref',
    cost: 800,
    after: null,
    requires: ['boss-sans-degat'],
    // La plus chère et la plus tardive des cinq : c'est la seule qui met
    // l'esquive et le dégât en tension directe. On la donne à qui a déjà prouvé
    // qu'il savait esquiver, sinon elle ne produit que des morts.
  },

  /* ── AMÉLIORATIONS PERMANENTES DE SECONDAIRE ──────────────────────────────
     Deux nœuds qui affûtent le clic droit pour TOUTE run, quelle que soit
     l'arme équipée. Ils portent la donnée dans `secondaryCooldown` /
     `secondaryDamage` ; `metaBonuses` la lit et `metaModifiers` la traduit en
     modificateur, exactement comme les cartes du pool.

     ⚠️ Ils ne pèsent PAS sur la jauge de +35 % (`compositePower` ne somme que
     les trois MetaStat), et c'est légitime : l'axe des secondaires est tenu sous
     la primaire par `validateSecondaries`, donc l'améliorer n'achète pas la
     victoire — ça affûte un outil tactique, pas la puissance de l'axe équilibré.
     C'est la même nature que débloquer une arme secondaire : un CHOIX de jeu,
     pas un pourcentage sur la courbe. Leur borne dure vit dans `computeStats`
     (plafond des dégâts ×2,5, plancher de recharge ×0,4), partagée avec les
     cartes — un joueur qui cumule nœuds, cartes et FANAL bute dessus, jamais
     au-delà. ── */
  {
    kind: 'option',
    currency: 'credits',
    id: 'sec-servocommande',
    name: 'Servocommande de recharge',
    text: 'Recharge des armes secondaires −15 % en permanence.',
    option: 'unlock',
    grants: 'secondaire-servocommande',
    secondaryCooldown: 0.15,
    cost: 300,
    after: null,
    requires: ['monde-1-franchi'],
    // Gaté par le monde 1, comme la plupart des secondaires : un joueur qui n'a
    // pas encore pris la mesure du clic droit ne sait pas ce que « recharge plus
    // courte » lui achète. Le prix reste bas — c'est un affûtage, pas une arme.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'sec-amplificateur',
    name: 'Amplificateur d’ogives',
    text: 'Dégâts des armes secondaires +20 % en permanence.',
    option: 'unlock',
    grants: 'secondaire-amplificateur',
    secondaryDamage: 0.2,
    cost: 300,
    after: null,
    requires: ['chasseur-400'],
    // « 400 ennemis dans une run » désigne le joueur qui déclenche déjà sa
    // secondaire souvent et à bon escient : c'est à lui que des ogives plus
    // lourdes rapportent, pas à celui qui l'oublie.
  },

  /* ── BOUCLIER PERMANENT ────────────────────────────────────────────────────
     La VOIE MÉTA du bouclier, en regard de la carte de run `plaque-bouclier`. La
     carte offre la barrière le temps d'une partie ; ces nœuds la rendent
     PERMANENTE et l'affûtent. Ils portent la donnée dans `shieldCharges` /
     `shieldRegen` ; `metaBonuses` la lit, `metaModifiers` la traduit en
     `maxShield`/`shieldRegen`, exactement comme la carte.

     ⚠️ Même statut HORS JAUGE que les secondaires (`compositePower` ne somme que
     les trois MetaStat) : le bouclier est de la survie défensive, pas de la
     puissance sur l'axe équilibré. Sa borne dure vit dans `computeStats`
     (`STAT_CAPS.maxShield = 4`), partagée avec la carte — méta + carte butent sur
     le même plafond, jamais au-delà. Payés en CRÉDITS, comme tout ce qui débloque
     un OUTIL plutôt que de verser un pourcentage sur la courbe. ── */
  {
    kind: 'option',
    currency: 'credits',
    id: 'bouclier-generateur',
    name: 'Générateur de champ',
    text: 'Une charge de bouclier, en permanence, sur toutes tes runs.',
    option: 'unlock',
    grants: 'bouclier-generateur',
    shieldCharges: 1,
    cost: 300,
    after: null,
    // Gaté par le monde 1 : le bouclier se comprend une fois qu'on a pris
    // quelques coups. Avant, le joueur ne sait pas ce qu'une charge lui achète.
    requires: ['monde-1-franchi'],
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'bouclier-condensateur',
    name: 'Condensateur de champ',
    text: 'Une charge de bouclier de plus, et une régénération plus vive.',
    option: 'unlock',
    grants: 'bouclier-condensateur',
    shieldCharges: 1,
    shieldRegen: 0.5,
    cost: 400,
    after: null,
    // Gaté par « une run sans mourir » : la récompense d'un joueur qui a DÉJÀ
    // prouvé qu'il sait survivre, pas un filet vendu à qui s'enfonce. C'est ce qui
    // le garde du côté de la maîtrise plutôt que de la béquille.
    requires: ['run-sans-mort'],
  },

  /* ── BOMBES ───────────────────────────────────────────────────────────────
     Une seule bombe est choisie par run (§6.3). Ces deux nœuds n'ajoutent donc
     pas de bombes, ils ajoutent des RÉPONSES possibles à la même touche : les
     trois ne diffèrent pas en puissance mais en durée d'invulnérabilité et en
     effet de bord. ÉCLAT reste disponible d'office. ── */
  {
    kind: 'option',
    currency: 'credits',
    id: 'bombe-brisant',
    name: 'Bombe BRISANT',
    text: '0,90 s d’invulnérabilité, colonne de 260 u, 3 000 dégâts sur 2,2 s.',
    option: 'unlock',
    grants: 'bombe-brisant',
    cost: 500,
    after: null,
    requires: ['monde-1-franchi'],
    // Le compromis est lisible d'un coup d'œil : la moitié de l'invulnérabilité
    // d'ÉCLAT contre des dégâts de boss. C'est un choix, pas une amélioration —
    // et il se paie en secondes de survie, la monnaie la plus chère du jeu.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'bombe-temps-mort',
    name: 'Bombe TEMPS MORT',
    text: '1,20 s d’invulnérabilité, balles à 25 % de vitesse pendant 3,5 s, +50 % de valeur de frôlement.',
    option: 'unlock',
    grants: 'bombe-temps-mort',
    cost: 700,
    after: null,
    requires: ['frolement-200'],
    // Gaté par le frôlement, qu'elle récompense : c'est la bombe qui ne sauve
    // pas mais qui rapporte, et elle n'a de sens qu'entre les mains d'un joueur
    // qui frôle déjà. Elle ne détruit rien — la donner à un débutant serait lui
    // vendre une bombe qui ne fait rien.
  },

  /* ── VAISSEAU ─────────────────────────────────────────────────────────────
     Troisième coque, et même règle que les deux premières : un vaisseau
     ÉCHANGE, il n'ajoute pas. LANCE échange de la coque contre de la cadence,
     AEGIS de la vitesse contre du blindage, FANAL sa bombe contre sa
     secondaire. Le VULCAN de départ reste un choix défendable en fin d'arbre —
     c'est la condition pour que le jeu se termine à méta zéro. ── */
  {
    kind: 'option',
    currency: 'credits',
    id: 'vaisseau-fanal',
    name: 'Vaisseau FANAL',
    text: 'Aucune bombe, jamais. Recharge des armes secondaires deux fois plus rapide.',
    option: 'ship',
    grants: 'fanal',
    cost: 1800,
    after: null,
    requires: ['boss-sans-bombe'],
    // Le prérequis EST le vaisseau : on ouvre la coque sans bombe à qui a déjà
    // vaincu un boss sans en lâcher une. Le jalon ne mesure pas le mérite, il
    // mesure que le joueur sait ce qu'il achète — c'est la différence entre un
    // gate et une confirmation.
    //
    // ⚠️ Le seul nœud de l'arbre qui touche une constante d'équilibrage
    // (§6.3 : la recharge bornée à [2, 8] s). Le facteur 2 sort une secondaire
    // de la borne basse, ce qui est assumé : la borne existe pour qu'une arme
    // ne devienne pas une primaire bis, et FANAL a payé cette souplesse au prix
    // fort — il n'a plus de bouton « annuler ».
  },

  /* ── CONTRAINTES VOLONTAIRES ──────────────────────────────────────────────
     Le sas anti-grind (§6.9, leçon Nova Drift), et la partie de l'arbre qui va
     dans le sens INVERSE de la puissance : chacune de ces quatre options rend
     la run plus difficile et paie en crédits.

     C'est ce qui absorbe le désir de progression infinie sans toucher à un
     seul chiffre d'équilibrage. Un joueur qui a tout acheté et veut continuer à
     progresser a désormais quelque chose à faire, et ce quelque chose le rend
     MEILLEUR au lieu de rendre son vaisseau plus fort — exactement l'effet que
     le plafond de +35 % cherche à obtenir.

     Le cumul est plafonné à ×3,0 (§6.9) ; le droit de cumuler est lui-même un
     nœud, plus bas. ── */
  {
    kind: 'option',
    currency: 'credits',
    id: 'contrainte-amnesie',
    name: 'Amnésie',
    text: 'Les cartes n’affichent plus leur effet chiffré ni leur delta de DPS. +30 % de crédits.',
    option: 'unlock',
    grants: 'contrainte-amnesie',
    cost: 300,
    after: null,
    requires: ['monde-1-franchi'],
    // La plus douce des quatre, et la seule qui ne touche à aucun réglage : ce
    // n'est pas le jeu qui devient plus dur, c'est l'information qui disparaît.
    // Un joueur qui connaît le pool par cœur ne perd presque rien — c'est
    // précisément pourquoi elle rapporte le moins.
    //
    // ⚠️ Elle suspend volontairement la règle §6.7 (« effet chiffré exact,
    // jamais “tir plus rapide” »). L'exception ne tient que parce qu'elle est
    // choisie : la règle protège le joueur qui n'a rien demandé, pas celui qui
    // a payé pour s'en priver.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'contrainte-zero-achat',
    name: 'Zéro achat',
    text: 'La boutique de bord reste fermée pour toute la run. +35 % de crédits.',
    option: 'unlock',
    grants: 'contrainte-zero-achat',
    cost: 400,
    after: null,
    requires: ['austerite'],
    // Le jalon « Austérité » demande exactement ça sur un seul monde. Le nœud
    // vend le droit de le faire sur une run entière, et de se faire payer pour.
    // Un jalon qui devient une option jouable est la meilleure preuve qu'un
    // système de progression se tient : ce qu'on a appris devient ce qu'on
    // choisit.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'contrainte-maree-noire',
    name: 'Marée noire',
    text: '+35 % de cadence de tir ennemie. +45 % de crédits.',
    option: 'unlock',
    grants: 'contrainte-maree-noire',
    cost: 500,
    after: null,
    requires: ['monde-2-franchi'],
    // ⚠️ La seule des quatre qui touche un curseur que la DIFFICULTÉ touche
    // déjà (§6.10 : cadence ennemie 70 / 100 / 120 %). Elle doit s'appliquer
    // par-dessus, en multiplicatif, jamais en remplacement — sinon elle
    // ADOUCIRAIT le Vétéran, et une contrainte volontaire qui rend le jeu plus
    // facile tout en payant +45 % est un bug économique, pas une option.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'contrainte-coque-de-verre',
    name: 'Coque de verre',
    text: 'Une seule vie, et aucun gain de vie de la run. +80 % de crédits.',
    option: 'unlock',
    grants: 'contrainte-coque-de-verre',
    cost: 850,
    after: null,
    requires: ['run-sans-mort'],
    // La plus dure et la mieux payée. Gatée par « finir sans mourir » : le
    // joueur qui l'achète a déjà fait, une fois, ce que la contrainte exige de
    // lui à chaque run. On ne vend pas une contrainte à quelqu'un qui ne sait
    // pas encore qu'il peut la tenir — il la porterait comme une punition.
    //
    // Elle annule de fait toute la branche PV de l'arbre STAT. C'est cohérent :
    // le joueur qui la choisit a déjà dépassé le besoin auquel cette branche
    // répond, et l'arbre lui offre de le dire.
  },

  /* ── CONFORT ET ENTRAÎNEMENT ──────────────────────────────────────────────
     La famille qui ne donne rien en run, et c'est son intérêt : elle fait
     progresser le JOUEUR pendant que l'arbre fait semblant de faire progresser
     le vaisseau. Un shmup se gagne par la connaissance des patterns ; du
     contenu qui accélère cette connaissance rapproche le 1CC bien plus qu'un
     pourcentage, et sans coûter un point de la jauge.

     ⚠️ Aucun de ces modes ne verse de crédits, ne décroche de jalon, ni ne
     produit de score classé. Sans cette règle, rejouer le boss du monde 3 en
     boucle deviendrait la façon la plus rentable de jouer, et l'anti-farm des
     90 secondes (`MIN_RUN_SECONDS`) serait contourné par la porte de service. ── */
  /* ⚠️ Le nœud « Journal de bord » a été RETIRÉ. Sa fonction — consulter les
     fiches des ennemis rencontrés — est devenue une fonction de BASE : le
     REGISTRE DES MENACES, gratuit, accessible depuis l'accueil. Le laisser
     payant après ça en aurait fait un doublon qu'aucun joueur n'achète, donc
     un nœud fantôme. Le relevé des ennemis croisés (`seenEnemies`) est
     indépendant de tout achat et continue de se remplir en jouant. */
  {
    kind: 'option',
    currency: 'credits',
    id: 'salle-entrainement',
    name: 'Salle d’entraînement',
    text: 'Rejouer à volonté un boss déjà vaincu. Aucun crédit, aucun jalon, aucun score.',
    option: 'unlock',
    grants: 'salle-entrainement',
    cost: 400,
    after: null,
    requires: ['monde-1-franchi'],
    // « Déjà vaincu » n'est pas une formalité : un boss qu'on peut répéter sans
    // l'avoir jamais atteint transformerait la salle en raccourci de contenu.
    // Ici elle ne donne accès qu'à ce que le joueur a déjà vu — elle supprime
    // le trajet, pas l'épreuve.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'raccourci-secteurs',
    name: 'Accès aux secteurs franchis',
    text: 'Démarrer un entraînement à un monde déjà terminé. Aucun crédit, aucun jalon, aucun score.',
    option: 'unlock',
    grants: 'raccourci-secteurs',
    cost: 550,
    after: null,
    requires: ['monde-2-franchi'],
    // Le raccourci que le plan autorise dans les losanges, et le nœud le plus
    // dangereux de l'arbre si on l'implémente mal : une run démarrée au monde 3
    // qui verserait 120 crédits par monde et 200 par boss rendrait le début du
    // jeu inutile. Le mode est un ENTRAÎNEMENT, jamais une run — il n'écrit
    // rien dans la sauvegarde.
  },
  {
    kind: 'option',
    currency: 'credits',
    id: 'double-contrainte',
    name: 'Double contrainte',
    text: 'Cumuler deux contraintes volontaires sur la même run. Gains cumulés, plafond ×3,0.',
    option: 'unlock',
    grants: 'cumul-contraintes',
    cost: 900,
    after: null,
    requires: ['veteran'],
    // Le nœud terminal de l'arbre, et le seul gaté par le jalon le plus dur du
    // jeu — finir en Vétéran. C'est assumé : il ne donne rien qu'un joueur
    // puisse vouloir avant d'avoir épuisé le reste, et son unique fonction est
    // de donner une suite à celui qui a déjà tout acheté. Le plafond de ×3,0
    // vient du plan et n'est pas négociable ici : au-delà, le farm de crédits
    // redevient plus rentable que le jeu.
  },
];

export default tree;
